import React from 'react';
import moment from 'moment-timezone';
import {
    VictoryBar,
    VictoryAxis,
    VictoryLine,
    VictoryLabel,
    VictoryLegend,
    VictoryTooltip,
    LineSegment,
} from 'victory';
import { ticksEveryOtherHour, generateTickValues } from '../Timeline/Timeline.helpers';
import { TIME_FORMAT, TIME_SHORT_FORMAT } from '../../common/constants';
import {
    colorWhiteTransp,
    colorDeepSkyBlueTransp,
    colorEmerald,
    colorLightBlue,
    chartTheme,
} from '../../common/ChartTheme';
import { breakpoint } from 'theme/breakpoint';

export const legendPointSize = 9;
export const legendOptions = {
    totalGeneration: {
        name: 'Total Generation',
        symbol: {
            fill: colorWhiteTransp,
            type: 'square',
            size: legendPointSize,
        },
    },
    observedLightning: {
        name: 'Observed Lightning',
        symbol: {
            fill: colorDeepSkyBlueTransp,
            type: 'square',
            size: legendPointSize,
        },
    },
    windSpeed: {
        name: 'Wind Speed',
        symbol: { fill: colorEmerald, type: 'minus', size: legendPointSize },
    },
    forecastWaveHeight: {
        name: 'Forecast Wave Height',
        symbol: {
            fill: colorLightBlue,
            type: 'minus',
            size: legendPointSize,
        },
    },
};

export const keyToTZ = (d) => {
    if (!d.key) {
        return null;
    }
    return moment.utc(d.key).valueOf();
};

/*
TODO: need to validate this with actual data. Timestamp for weekly data from backend would be 07.10.2019
but this represents data from 30.09-06.10. Need to subtract 1 week from the original timestamp in order to display the bars
on the weekly graph in their correct x-axis position.
*/
export const keyToWeeklyTZ = (d) => {
    if (!d.key) {
        return null;
    }
    return moment.utc(d.key).subtract(1, 'weeks');
};

export const roundLightningKey = (d) => {
    if (!d.key) {
        return null;
    }

    return moment
        .utc(d.key)
        .startOf('hour')

        .valueOf();
};

export const getLightningBar = (observedLightningData, chartWidth, chartPadding) => {
    const { left, right } = chartPadding;
    const lightningBarWidth = (chartWidth - left - right) / 24;
    return (
        observedLightningData &&
        observedLightningData.length > 0 && (
            <VictoryBar
                key="3"
                name="lightning-chart"
                style={{
                    data: {
                        fill: colorDeepSkyBlueTransp,
                        width: lightningBarWidth,
                    },
                }}
                x={roundLightningKey}
                y={() => 1}
                data={observedLightningData}
            />
        )
    );
};

export const getLegend = (legendData, chartWidth, chartHeight) => {
    const isNarrow = chartWidth < 450;
    const itemWidth = 150;
    const gutter = 20;
    const legendX = isNarrow ? 0 : (chartWidth - itemWidth * legendData.length) / 2;
    const legendY = chartHeight + (isNarrow ? 10 : 30);

    const itemsPerRow = isNarrow ? 2 : 3;

    return (
        <VictoryLegend
            x={legendX}
            y={legendY}
            orientation="horizontal"
            gutter={gutter}
            data={legendData}
            itemsPerRow={itemsPerRow}
        />
    );
};
export const getBottomAxis = (chartWidth, chartOffsetX, internalChartDay, translate = true) => (
    <VictoryAxis
        orientation="bottom"
        name="time-axis"
        scale="time"
        tickFormat={ticksEveryOtherHour(chartWidth < breakpoint.extraSmall ? TIME_SHORT_FORMAT : TIME_FORMAT)}
        tickValues={generateTickValues(internalChartDay)}
        tickLabelComponent={<VictoryLabel />}
        gridComponent={
            <LineSegment type="grid" transform={translate ? `translate(${-chartOffsetX},0)` : 'translate(0)'} />
        }
        tickCount={24}
    />
);

export const getWindAxis = (chartWidth, maximum) => {
    const orientation = !chartWidth ? 'left' : 'right';
    const x = !chartWidth ? 51 : chartWidth - 51;
    return (
        <VictoryAxis
            key="wind-speed-axis"
            name="wind-speed-axis"
            orientation={orientation}
            axisLabelComponent={<VictoryLabel verticalAnchor="start" y={0} x={x} angle={0} />}
            label="Wind Speed [m/s]"
            dependentAxis
            tickFormat={(t) => t * maximum}
        />
    );
};

export const getWindLine = (data, maximum) =>
    data.length > 0 && (
        <VictoryLine
            key="2"
            name="wind-speed-chart"
            style={{
                data: { stroke: colorEmerald, strokeWidth: 3 },
            }}
            y={(d) => {
                const scaledValue = d.value / maximum;
                return d.value === null ? null : scaledValue;
            }}
            labels={({ datum }) =>
                datum.value === null ? 'Data Unavailable' : `${Math.round(datum.value * 100) / 100} m/s`
            }
            labelComponent={<VictoryTooltip {...chartTheme.tooltip} />}
            x={keyToTZ}
            data={data}
        />
    );
